import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { format } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import SuspenseLoader from "src/components/SuspenseLoader/SuspenseLoader";
import { SidebarContext } from "src/contexts/SidebarContext";
import { useAuth0 } from "@auth0/auth0-react";
import { cancelSubscription } from "src/api/cancelSubscription";
import { CanceledSubscription } from "src/types/CanceledSubscription";
import { useNavigate } from "react-router";
import { getClientReceipts } from "src/api/getClientReceipts";

interface Column {
  id: "month" | "amount" | "view";
  label: string;
  minWidth?: number;
}

const columns: readonly Column[] = [
  { id: "month", label: "month" },
  { id: "amount", label: "amount" },
  { id: "view", label: "view" },
];

function BillingTable() {
  const { clientInfo, remainingRequests, setIsDataLoaded, fetchRemainingRequests, receipts, loadingReceipts } = useContext(SidebarContext);
  const { user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  //Sate for store canceled ubscription data
  const [canceledSubscription, setCanceledSubscription] = useState<CanceledSubscription | null>(null);
  const [isNoSubscription, setIsNoSubscription] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const [showCancelBox, setShowCancelBox] = useState(false);

  if (loadingReceipts) {
    return <SuspenseLoader />;
  }

  const handleShowCancelBox = () => {
    setShowCancelBox(true);
  };

  const formatDate = (timestamp: number) => {
    return format(new Date(timestamp), "MM/dd/yy");
  };

  //Function to canceled subscription data data
  const handleCancelSubscription = async (cancelImmediately: boolean) => {
    try {
      if (!clientInfo?.clientId) return;
      const accessAuthToken = await getAccessTokenSilently();

      const canceledSubscriptionData = await cancelSubscription(accessAuthToken, clientInfo.clientId, cancelImmediately);
      //Update canceled subscription data state to render in the dashboard
      setCanceledSubscription(canceledSubscriptionData);
      setIsDataLoaded(true);

      if (canceledSubscriptionData && canceledSubscriptionData.status === "canceled") {
        await fetchRemainingRequests();
        navigate("/dashboard/home/cancel");
      }
    } catch (error) {
      console.error("Failed to get client stats data", error);
    }
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  console.log("receipts", receipts);

  // console.log("DATA DATA DATA", canceledSubscription);

  return (
    <>
      <Box
        sx={{
          // borderRadius: "5px",
          // border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]} `,
          "@media (min-width: 1280px)": {
            width: "60%",
            margin: "0 auto",
          },
        }}
      >
        <TableContainer
          sx={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            overflowY: "scroll",
            pl: 1,
            pr: 1,

            "@media (min-width: 1280px)": {
              width: "100%",
            },
          }}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align="justify"
                    sx={{
                      fontFamily: "Roboto,arial",
                      backgroundColor: PureLightThemeCaaS.palette.background.default,
                      color: PureLightThemeCaaS.colors.alpha.white[100],
                      borderBottomColor: PureLightThemeCaaS.colors.alpha.white[30],
                      pt: 2,
                      pb: 2,
                      pl: column.id === "view" ? 2 : 0,
                      pr: 0,
                      "@media (min-width: 1280px)": {
                        minWidth: 100,
                      },
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {receipts.map((receipt) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={receipt.invoice_id}
                  sx={{
                    fontFamily: "Roboto,arial",
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    borderBottomColor: PureLightThemeCaaS.colors.alpha.white[30],
                  }}
                >
                  <TableCell
                    align="justify"
                    sx={{
                      fontFamily: "Roboto,arial",
                      color: PureLightThemeCaaS.colors.alpha.white[100],
                      borderBottomColor: PureLightThemeCaaS.colors.alpha.white[30],
                      p: 0,
                    }}
                  >
                    {new Date(receipt.date).toLocaleString("en-US", { month: "long" })}
                  </TableCell>

                  <TableCell
                    align="justify"
                    sx={{
                      fontFamily: "Roboto,arial",
                      color: PureLightThemeCaaS.colors.alpha.white[100],
                      borderBottomColor: PureLightThemeCaaS.colors.alpha.white[30],
                      p: 0,
                    }}
                  >
                    ${receipt.amount.toFixed(2)}
                  </TableCell>

                  <TableCell
                    align="justify"
                    sx={{
                      fontFamily: "Roboto,arial",
                      color: PureLightThemeCaaS.colors.alpha.white[100],
                      borderBottomColor: PureLightThemeCaaS.colors.alpha.white[30],
                      p: 0,
                    }}
                  >
                    <Button
                      variant="text"
                      color="warning"
                      onClick={() => window.open(receipt.receipt_url, "_blank")}
                      sx={{
                        fontFamily: "Roboto,arial",
                        textTransform: "none",
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              width: "100%",
              mt: 1,
            }}
          >
            {!showCancelBox && remainingRequests?.subscription_status === "active" && (
              <Button
                size="medium"
                variant="text"
                onClick={handleShowCancelBox}
                sx={{ pl: 0, fontSize: "16px", color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, fontFamily: "Roboto, Arial", textTransform: "none" }}
              >
                Cancel Account
              </Button>
            )}

            {/* Cancel subscription box */}
            {showCancelBox && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "400px",
                  height: "auto",
                  boxShadow: "none",
                  borderRadius: 0.5,
                  // mt: 3,
                  gap: 2,
                  p: 2,
                  pt: 0,
                }}
              >
                <Button
                  size="medium"
                  variant="text"
                  onClick={handleShowCancelBox}
                  sx={{ pl: 0, fontSize: "16px", color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, fontFamily: "Roboto, Arial", textTransform: "none" }}
                >
                  Cancel Account
                </Button>
                {/* Checkbox for disabled button or not */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      color="warning"
                      sx={{
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: "Roboto, Arial",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                      }}
                    >
                      By checking this box I acknowledge I will no longer have access to the API or its endpoints.
                    </Typography>
                  }
                />
                {/* Cancel subscription button */}
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleCancelSubscription(true)}
                  disabled={!isChecked || isNoSubscription} //Disabled button when checkbox is not checked
                  sx={{
                    fontFamily: "Roboto, Arial",
                    textTransform: "none",
                    "&:disabled": {
                      backgroundColor: `${PureLightThemeCaaS.colors.alpha.orange[70]}`, // Disabled background color
                      color: `${PureLightThemeCaaS.colors.alpha.white[50]}`, // Disabled text color
                    },
                  }}
                >
                  Cancel account
                </Button>
              </Box>
            )}
          </Box>
        </TableContainer>
      </Box>
    </>
  );
}

export default BillingTable;
